import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { fg } from '@confluence/feature-gating';

import { ContentModeQuery } from '../graphql/ContentModeQuery.graphql';
import { serverToUiContentMode, queryStatusFor, type UiContentMode } from '../utils/contentMode';

/**
 * Hook to get the editor content mode value for a particular page/blog from the backend.
 */
export function useEditorContentModeValue({ contentId }: { contentId?: string }): {
	mode: UiContentMode;
	isLoading: boolean;
	error: unknown | null;
} {
	const isLivePage = useIsCurrentPageLive();
	const isEditorPage = useIsEditorPage();

	const isDraft =
		!isLivePage && Boolean(isEditorPage) && fg('platform_editor_content_mode_button_mvp');
	const status = queryStatusFor(isDraft);

	/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	const { data, error, loading } = useQueryInterop<any>(ContentModeQuery, {
		variables: { contentId, isDraft, status: [status] },
		fetchPolicy: 'cache-and-network',
		returnPartialData: true,
		skip: !fg('platform_editor_content_mode_button_mvp') || !contentId, // avoid starting network flow
	});

	let serverModeRaw: string | undefined;

	if (fg('platform_editor_content_mode_button_mvp')) {
		const contentProps = data?.content?.nodes?.[0]?.contentProperties;

		if (isDraft) {
			serverModeRaw = contentProps?.draft?.contentMode;
		} else {
			serverModeRaw = contentProps?.latest?.contentMode;
		}
	} else {
		serverModeRaw = undefined;
	}

	const mode: UiContentMode = serverToUiContentMode(serverModeRaw);

	return useMemo(
		() => ({
			mode,
			isLoading: fg('platform_editor_content_mode_button_mvp') ? Boolean(loading) : false,
			error: fg('platform_editor_content_mode_button_mvp') ? (error ?? null) : null,
		}),
		[mode, loading, error],
	);
}
